import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Bell, ChevronRight, Eye, Loader2, ShieldAlert } from "lucide-react";
import { RiskBadge } from "../Badges";

export default function ApprovalQueue({ readOnly = false }) {
    const [items, setItems] = useState(null); // null = loading, [] = empty
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        api.get("/dashboard/approval-queue")
            .then((r) => !cancelled && setItems(r.data || []))
            .catch((err) => {
                if (cancelled) return;
                if (err?.response?.status !== 401 && err?.response?.status !== 403) {
                    setError(err?.response?.data?.detail || err.message || "Failed to load approval queue");
                }
                setItems([]);
            });
        return () => { cancelled = true; };
    }, []);

    // Build Prompt #13 — Strategic Oversight sees the queue (volume signal) but cannot act
    // on it. We swap the strident pulsing border for a calmer Snow-on-Jet treatment.
    const containerCls = readOnly
        ? "border border-[#2A2A2A] bg-jet-elev1"
        : "border-2 border-signal bg-signal/5 animate-pulse-signal";
    const headerCls = readOnly ? "border-b border-[#2A2A2A]" : "border-b border-signal/40";

    return (
        <div
            data-testid="approval-queue-panel"
            data-readonly={readOnly ? "true" : "false"}
            className={containerCls}
            style={readOnly ? undefined : { animationDuration: "2.5s" }}
        >
            <div className={`${headerCls} px-5 py-3 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                    <ShieldAlert className={`w-4 h-4 ${readOnly ? "text-snow-dim" : "text-signal"}`} />
                    <span className={`overline ${readOnly ? "" : "text-signal"}`}>
                        Approval Queue {readOnly ? "— Read-Only View" : "— Action Required"}
                    </span>
                    {readOnly && (
                        <span
                            data-testid="approval-queue-readonly-badge"
                            className="inline-flex items-center gap-1 border border-snow-dim text-snow-dim px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.2em]"
                        >
                            <Eye className="w-2.5 h-2.5" /> Oversight
                        </span>
                    )}
                </div>
                <span className={`text-xs font-mono tabular-nums ${readOnly ? "text-snow-dim" : "text-signal"}`}>
                    {items === null ? "…" : `${items.length} pending`}
                </span>
            </div>

            {items === null ? (
                <div className="px-5 py-8 flex items-center gap-2 text-snow-dim text-xs font-mono">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading queue…
                </div>
            ) : error ? (
                <div className="px-5 py-4 text-xs text-red-400 font-mono">{error}</div>
            ) : items.length === 0 ? (
                <div className="px-5 py-8 text-snow-muted text-xs font-mono text-center" data-testid="approval-queue-empty">
                    Queue clear. No gates are awaiting executive sign-off right now.
                </div>
            ) : (
                <ul className={readOnly ? "divide-y divide-[#2A2A2A]" : "divide-y divide-signal/20"} data-testid="approval-queue-list">
                    {items.map((q) => {
                        const rowCls = readOnly
                            ? "px-5 py-3 flex items-center gap-4 hover:bg-white/[0.03] transition-colors group"
                            : "px-5 py-3 flex items-center gap-4 hover:bg-signal/10 transition-colors group";
                        return (
                        <li key={q.instance_id}>
                            <Link
                                to={`/products/${q.product_id}?gate=${q.instance_id}`}
                                data-testid={`approval-queue-item-${q.instance_id}`}
                                className={rowCls}
                            >
                                <Bell className={`w-4 h-4 ${readOnly ? "text-snow-dim" : "text-signal animate-pulse-signal"} shrink-0`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`font-mono font-bold tabular-nums shrink-0 ${readOnly ? "text-snow" : "text-signal"}`}>
                                            G{String(q.gate_id).padStart(2, "0")}
                                        </span>
                                        <span className="font-mono text-sm text-snow truncate">{q.gate_name}</span>
                                        <span className="text-snow-muted text-xs">·</span>
                                        <span className="font-mono text-xs text-snow-dim truncate">{q.product_name}</span>
                                        <RiskBadge tier={q.risk_tier} />
                                    </div>
                                    <div className="text-[11px] text-snow-muted mt-1 font-mono uppercase tracking-wider">
                                        {q.deliverable_count} deliverable{q.deliverable_count === 1 ? "" : "s"} ·
                                        Required: {(q.required_roles || []).join(", ") || "—"}
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div
                                        className={`heading-display text-2xl tabular-nums ${readOnly ? "text-snow" : "text-signal"}`}
                                        data-testid={`approval-queue-days-${q.instance_id}`}
                                    >
                                        {q.days_waiting}
                                    </div>
                                    <div className="text-[9px] text-snow-muted font-mono uppercase tracking-[0.2em]">
                                        {q.days_waiting === 1 ? "Day" : "Days"} Waiting
                                    </div>
                                </div>
                                <ChevronRight className={`w-4 h-4 text-snow-muted ${readOnly ? "group-hover:text-snow" : "group-hover:text-signal"} transition-colors shrink-0`} />
                            </Link>
                        </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
